import { initializeApp } from 'firebase/app';
import { getFirestore, disableNetwork } from '../services/firestoreCompat';
import { getAuth } from 'firebase/auth';

/**
 * Saheeh Firebase project — config is env-driven so this codebase NEVER
 * accidentally talks to a different Firebase project (e.g. a customer's
 * live production system). The guard below refuses to start if anyone
 * accidentally pastes credentials whose projectId matches a known
 * forbidden value.
 *
 * Wire-up:
 *   1. Create a fresh Firebase project for Saheeh (e.g. `saheeh-pos`).
 *   2. Add a Web app to it; copy the SDK config snippet.
 *   3. Set these env vars in Vercel / `.env.local`:
 *        REACT_APP_FIREBASE_API_KEY  (must start with "AIza")
 *        REACT_APP_FIREBASE_AUTH_DOMAIN
 *        REACT_APP_FIREBASE_PROJECT_ID
 *        REACT_APP_FIREBASE_STORAGE_BUCKET
 *        REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *        REACT_APP_FIREBASE_APP_ID
 *        REACT_APP_FIREBASE_MEASUREMENT_ID
 *        REACT_APP_FIREBASE_DATABASE_URL  (optional, for Realtime DB)
 *
 * Without those env vars, the app still boots (so the user can log in via
 * Supabase auth + see the UI shell), but Firestore is put into "offline"
 * mode — reads return empty from cache instead of retrying network calls
 * forever and freezing every page on a spinner. Writes are silently queued
 * and dropped.
 */
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY ?? '';
export const HAS_VALID_FIREBASE_CONFIG = apiKey.startsWith('AIza');

const firebaseConfig = {
  apiKey:            apiKey || 'STUB',
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? 'stub.firebaseapp.com',
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID ?? 'saheeh-stub',
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? 'stub.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? '0',
  appId:             process.env.REACT_APP_FIREBASE_APP_ID ?? 'STUB',
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL:       process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

// SAFETY GUARD — buyers can add Firebase project IDs to this array to refuse
// boot against known-wrong projects (e.g. a staging project that should never
// receive production traffic). Default: empty.
const FORBIDDEN_PROJECT_IDS: string[] = [];

if (FORBIDDEN_PROJECT_IDS.includes((firebaseConfig.projectId || '').toLowerCase())) {
  throw new Error(
    '[Saheeh] Firebase config points at a forbidden project. ' +
    'Refusing to start — set REACT_APP_FIREBASE_* to a valid Firebase project for this deployment.',
  );
}

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

if (!HAS_VALID_FIREBASE_CONFIG) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Saheeh] Firebase is not configured — putting Firestore into offline mode. ' +
    'Reads will return empty snapshots immediately. Writes will be silently queued. ' +
    'Set REACT_APP_FIREBASE_* env vars in Vercel to enable live Firestore.',
  );
  // Fire-and-forget — disableNetwork is idempotent and safe to call without awaiting.
  disableNetwork(db).catch(() => { /* offline-on-init is allowed to fail */ });
}

export default app;
